import { useState, useEffect, useMemo, useCallback } from 'react';
import {
    useGetIdentityRolesQuery,
    useGetPermissionCatalogQuery,
    useGetIdentityRoleSettingsQuery,
    useGetIdentityRolePermissionsQuery,
    useSetIdentityRolePermissionsMutation,
    useSetIdentityRoleSettingsMutation,
    useCreateIdentityRoleMutation
} from '../../../../../api/endpoints/permissionManagement';
import { DATA_SCOPES, USER_TYPES, permissionCode } from '../../constants';

const resourceOf = (code) => code.substring(0, code.lastIndexOf('.'));
const actionOf = (code) => code.substring(code.lastIndexOf('.') + 1);

// Satır için varsayılan kapsam: bayi rollerinde ana bayi, diğerlerinde tümü (kaynağın desteklediği en yakın değer)
const defaultScopeFor = (resource, assignableUserType) => {
    const supported = resource?.supportedScopes || [];
    if (supported.length === 0) return DATA_SCOPES.ALL;
    const preferred = assignableUserType === USER_TYPES.DEALER ? DATA_SCOPES.MAIN_DEALER : DATA_SCOPES.ALL;
    if (supported.includes(preferred)) return preferred;
    return Math.max(...supported);
};

const buildGrants = (permissions = []) => {
    const grants = {};
    permissions.forEach((p) => {
        const resource = resourceOf(p.code);
        if (!grants[resource]) grants[resource] = { actions: [], scope: p.scope };
        grants[resource].actions.push(actionOf(p.code));
        grants[resource].scope = Math.max(grants[resource].scope, p.scope);
    });
    return grants;
};

export const useRoleManagementData = () => {
    const [selectedRoleId, setSelectedRoleId] = useState(null);
    const [roleFilter, setRoleFilter] = useState('');
    const [grants, setGrants] = useState({});
    const [originalGrants, setOriginalGrants] = useState({});

    const { data: roles = [], isLoading: rolesLoading, error: rolesError } = useGetIdentityRolesQuery();
    const { data: catalog = [], isLoading: catalogLoading } = useGetPermissionCatalogQuery();
    const { data: settings, isFetching: settingsLoading } = useGetIdentityRoleSettingsQuery(selectedRoleId, { skip: !selectedRoleId });
    const { data: rolePermissions, isFetching: permissionsLoading } = useGetIdentityRolePermissionsQuery(selectedRoleId, { skip: !selectedRoleId });

    const [setRolePermissions, { isLoading: savingPermissions }] = useSetIdentityRolePermissionsMutation();
    const [setRoleSettings, { isLoading: savingSettings }] = useSetIdentityRoleSettingsMutation();
    const [createRole, { isLoading: creatingRole }] = useCreateIdentityRoleMutation();

    const resourceMap = useMemo(() => {
        const map = {};
        catalog.forEach((module) => module.resources.forEach((resource) => (map[resource.code] = resource)));
        return map;
    }, [catalog]);

    useEffect(() => {
        if (!rolePermissions) return;
        const initial = buildGrants(rolePermissions.permissions);
        setGrants(initial);
        setOriginalGrants(initial);
    }, [rolePermissions]);

    const filteredRoles = useMemo(() => {
        const term = roleFilter.trim().toLocaleLowerCase('tr');
        const sorted = [...roles].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        if (!term) return sorted;
        return sorted.filter((r) => (r.name || '').toLocaleLowerCase('tr').includes(term));
    }, [roles, roleFilter]);

    const isSystemRole = !!(settings?.isSystem || rolePermissions?.isSystem);

    const isDirty = useMemo(() => {
        const normalize = (g) =>
            JSON.stringify(
                Object.keys(g)
                    .filter((k) => g[k].actions.length > 0)
                    .sort()
                    .map((k) => [k, [...g[k].actions].sort(), g[k].scope])
            );
        return normalize(grants) !== normalize(originalGrants);
    }, [grants, originalGrants]);

    const toggleAction = useCallback(
        (resourceCode, action) => {
            setGrants((prev) => {
                const current = prev[resourceCode] || { actions: [], scope: defaultScopeFor(resourceMap[resourceCode], settings?.assignableUserType) };
                const actions = current.actions.includes(action) ? current.actions.filter((a) => a !== action) : [...current.actions, action];
                return { ...prev, [resourceCode]: { ...current, actions } };
            });
        },
        [resourceMap, settings]
    );

    const setResourceActions = useCallback(
        (resourceCode, checked) => {
            const resource = resourceMap[resourceCode];
            setGrants((prev) => {
                const current = prev[resourceCode] || { actions: [], scope: defaultScopeFor(resource, settings?.assignableUserType) };
                return { ...prev, [resourceCode]: { ...current, actions: checked ? resource.actions.map((a) => a.action) : [] } };
            });
        },
        [resourceMap, settings]
    );

    const setModuleActions = useCallback(
        (module, checked) => {
            setGrants((prev) => {
                const next = { ...prev };
                module.resources.forEach((resource) => {
                    const current = next[resource.code] || { actions: [], scope: defaultScopeFor(resource, settings?.assignableUserType) };
                    next[resource.code] = { ...current, actions: checked ? resource.actions.map((a) => a.action) : [] };
                });
                return next;
            });
        },
        [settings]
    );

    const setResourceScope = useCallback((resourceCode, scope) => {
        setGrants((prev) => ({ ...prev, [resourceCode]: { ...(prev[resourceCode] || { actions: [] }), scope } }));
    }, []);

    const resetPermissions = useCallback(() => setGrants(originalGrants), [originalGrants]);

    const savePermissions = useCallback(async () => {
        const permissions = [];
        Object.entries(grants).forEach(([resourceCode, grant]) => {
            grant.actions.forEach((action) => permissions.push({ code: permissionCode(resourceCode, action), scope: grant.scope }));
        });
        const result = await setRolePermissions({ roleId: selectedRoleId, permissions }).unwrap();
        return result;
    }, [grants, selectedRoleId, setRolePermissions]);

    const saveSettings = useCallback(
        async (values) => {
            return setRoleSettings({ roleId: selectedRoleId, ...values }).unwrap();
        },
        [selectedRoleId, setRoleSettings]
    );

    const createNewRole = useCallback(
        async ({ name, description, moduleCode, priority, assignableUserType }) => {
            const role = await createRole({ name }).unwrap();
            await setRoleSettings({ roleId: role.id, description, moduleCode, priority, assignableUserType, isActive: true }).unwrap();
            setSelectedRoleId(role.id);
            return role;
        },
        [createRole, setRoleSettings]
    );

    return {
        roles: filteredRoles,
        rolesLoading,
        rolesError,
        roleFilter,
        setRoleFilter,
        selectedRoleId,
        setSelectedRoleId,
        selectedRole: roles.find((r) => r.id === selectedRoleId) || null,
        catalog,
        catalogLoading,
        settings,
        settingsLoading,
        permissionsLoading,
        isSystemRole,
        grants,
        isDirty,
        toggleAction,
        setResourceActions,
        setModuleActions,
        setResourceScope,
        resetPermissions,
        savePermissions,
        savingPermissions,
        saveSettings,
        savingSettings,
        createNewRole,
        creatingRole
    };
};
